// California PUMS Service - Fetch ALL Data from pums_combined_ca
import { supabaseClient } from '../../../services/supabase';
import { FullPumaRecord } from './simplePumsService';

// California table uses same schema as Bay Area table, so we can reuse the interface
export type CaliforniaPumaRecord = FullPumaRecord;

export class CaliforniaPumsService {
  /**
   * Get ALL data from Supabase table - no filtering, just raw records
   * Uses pagination to ensure we get every single record
   * @param onProgress Optional callback to report loading progress (0-100)
   */
  async getAllCaliforniaData(onProgress?: (progress: number) => void): Promise<CaliforniaPumaRecord[]> {
    try {
      console.log('🔍 Fetching ALL California PUMA data from Supabase (with optimized pagination)...');
      
      let allData: any[] = [];
      let page = 0;
      const pageSize = 50000; // Increased from 10k to 50k for faster loading
      let hasMore = true;
      
      // Estimate total pages (assuming ~2-3 million records, so ~40-60 pages max)
      // We'll update this as we fetch more pages
      let estimatedTotalPages = 50; // Conservative estimate
      let totalPagesFetched = 0;

      while (hasMore) {
        const startTime = Date.now();
        console.log(`📄 Fetching page ${page + 1} (records ${page * pageSize + 1} to ${(page + 1) * pageSize})...`);
        
        const { data, error } = await supabaseClient
          .from('pums_combined_ca')
          .select('*')
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (error) {
          console.error('❌ Supabase query error:', error);
          console.error('Error details:', {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          });
          throw error;
        }

        const fetchTime = Date.now() - startTime;
        
        if (!data || data.length === 0) {
          hasMore = false;
          console.log(`📄 Page ${page + 1}: No more data`);
          // Update progress to 100% when done
          if (onProgress) {
            onProgress(100);
          }
        } else {
          allData = allData.concat(data);
          totalPagesFetched = page + 1;
          console.log(`📄 Page ${page + 1}: Fetched ${data.length} records in ${fetchTime}ms (total: ${allData.length})`);
          
          // If we got fewer records than pageSize, we've reached the end
          if (data.length < pageSize) {
            hasMore = false;
            // Update progress to 100% when done
            if (onProgress) {
              onProgress(100);
            }
          } else {
            // Estimate progress: if we're still getting full pages, estimate based on current page
            // Progress = (pages fetched / estimated total) * 90% (leave 10% for final processing)
            const progress = Math.min(90, Math.round((totalPagesFetched / estimatedTotalPages) * 90));
            if (onProgress) {
              onProgress(progress);
            }
            
            // If we've fetched more pages than estimated, increase the estimate
            if (totalPagesFetched >= estimatedTotalPages) {
              estimatedTotalPages = totalPagesFetched + 10; // Add buffer
            }
          }
          
          page++;
        }
      }

      console.log(`📊 Total fetched: ${allData.length} complete records from Supabase`);

      if (allData.length === 0) {
        console.log('⚠️ No data returned from Supabase');
        return [];
      }

      // Log the first record to see what columns we have
      console.log('📋 Sample record structure:', Object.keys(allData[0]));
      console.log('📋 Sample record data:', allData[0]);

      // Return all data - map to FullPumaRecord interface
      // Note: Table may have additional columns (like energy_burden, annual_energy_cost, etc.)
      // but we only extract the fields needed for FullPumaRecord
      const results: CaliforniaPumaRecord[] = allData.map((row: any) => ({
        serialno: row.serialno || row.SERIALNO || '',
        puma: row.puma ?? row.PUMA ?? 0,
        state: row.state ?? row.STATE ?? 6,
        adjinc: row.adjinc ?? row.ADJINC ?? 0,
        wgtp: row.wgtp ?? row.WGTP ?? 0,
        np: row.np ?? row.NP ?? 0,
        bld: row.bld ?? row.BLD ?? 0,
        elep: row.elep ?? row.ELEP ?? 0,
        fulp: row.fulp ?? row.FULP ?? 0,
        gasp: row.gasp ?? row.GASP ?? 0,
        hfl: row.hfl ?? row.HFL ?? 0,
        rntp: row.rntp ?? row.RNTP ?? 0,
        ten: row.ten ?? row.TEN ?? 0,
        valp: row.valp ?? row.VALP ?? 0,
        veh: row.veh ?? row.VEH ?? 0,
        yrblt: row.yrblt ?? row.YRBLT ?? row.BUILT ?? 0,
        fincp: row.fincp ?? row.FINCP ?? 0,
        grpip: row.grpip ?? row.GRPIP ?? 0,
        hincp: row.hincp ?? row.HINCP ?? 0,
        lngi: row.lngi ?? row.LNGI ?? 0,
        pwtgp: row.pwtgp ?? row.PWTGP ?? row.PWGTP ?? 0,
        agep: row.agep ?? row.AGEP ?? row.AGE ?? 0,
        eng: row.eng ?? row.ENG ?? 0,
        jwtrns: row.jwtrns ?? row.JWTRNS ?? 0,
        relshipp: row.relshipp ?? row.RELSHIPP ?? row.RELP ?? 0,
        schl: row.schl ?? row.SCHL ?? 0,
        sex: row.sex ?? row.SEX ?? 0,
        dis: row.dis ?? row.DIS ?? 0,
        hisp: row.hisp ?? row.HISP ?? 0,
        pincp: row.pincp ?? row.PINCP ?? 0,
        rac1p: row.rac1p ?? row.RAC1P ?? 0,
      }));

      console.log(`✅ Loaded ${results.length} complete records`);
      return results;
    } catch (error) {
      console.error('❌ Error fetching all California PUMA data:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        throw new Error(`Failed to fetch all California PUMA data: ${error.message}`);
      }
      throw new Error('Failed to fetch all California PUMA data');
    }
  }

  /**
   * Get data for specific PUMA codes (region-based query for faster loading)
   * @param pumaCodes Array of PUMA codes to fetch
   * @param onProgress Optional callback to report loading progress (0-100)
   */
  async getDataByPumaCodes(pumaCodes: number[], onProgress?: (progress: number) => void): Promise<CaliforniaPumaRecord[]> {
    if (pumaCodes.length === 0) {
      return [];
    }

    try {
      console.log(`🔍 Fetching California PUMA data for ${pumaCodes.length} PUMAs (region-based)...`);
      
      let allData: any[] = [];
      let page = 0;
      const pageSize = 50000;
      let hasMore = true;
      
      // Estimate total pages (region-specific is usually 1-2 pages)
      let estimatedTotalPages = 2;
      let totalPagesFetched = 0;

      while (hasMore) {
        const startTime = Date.now();
        console.log(`📄 Fetching page ${page + 1} (records ${page * pageSize + 1} to ${(page + 1) * pageSize})...`);
        
        const { data, error } = await supabaseClient
          .from('pums_combined_ca')
          .select('*')
          .in('puma', pumaCodes)
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (error) {
          console.error('❌ Supabase query error:', error);
          console.error('Error details:', {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          });
          throw error;
        }

        const fetchTime = Date.now() - startTime;
        
        if (!data || data.length === 0) {
          hasMore = false;
          console.log(`📄 Page ${page + 1}: No more data`);
          // Update progress to 100% when done
          if (onProgress) {
            onProgress(100);
          }
        } else {
          allData = allData.concat(data);
          totalPagesFetched = page + 1;
          console.log(`📄 Page ${page + 1}: Fetched ${data.length} records in ${fetchTime}ms (total: ${allData.length})`);
          
          if (data.length < pageSize) {
            hasMore = false;
            // Update progress to 100% when done
            if (onProgress) {
              onProgress(100);
            }
          } else {
            // Estimate progress: (pages fetched / estimated total) * 90% (leave 10% for final processing)
            const progress = Math.min(90, Math.round((totalPagesFetched / estimatedTotalPages) * 90));
            if (onProgress) {
              onProgress(progress);
            }
            
            // If we've fetched more pages than estimated, increase the estimate
            if (totalPagesFetched >= estimatedTotalPages) {
              estimatedTotalPages = totalPagesFetched + 2; // Add buffer
            }
          }
          
          page++;
        }
      }

      console.log(`📊 Total fetched: ${allData.length} records for region PUMAs`);

      if (allData.length === 0) {
        console.log('⚠️ No data returned from Supabase for selected region');
        return [];
      }

      // Map to FullPumaRecord interface (same as getAllCaliforniaData)
      const results: CaliforniaPumaRecord[] = allData.map((row: any) => ({
        serialno: row.serialno || row.SERIALNO || '',
        puma: row.puma ?? row.PUMA ?? 0,
        state: row.state ?? row.STATE ?? 6,
        adjinc: row.adjinc ?? row.ADJINC ?? 0,
        wgtp: row.wgtp ?? row.WGTP ?? 0,
        np: row.np ?? row.NP ?? 0,
        bld: row.bld ?? row.BLD ?? 0,
        elep: row.elep ?? row.ELEP ?? 0,
        fulp: row.fulp ?? row.FULP ?? 0,
        gasp: row.gasp ?? row.GASP ?? 0,
        hfl: row.hfl ?? row.HFL ?? 0,
        rntp: row.rntp ?? row.RNTP ?? 0,
        ten: row.ten ?? row.TEN ?? 0,
        valp: row.valp ?? row.VALP ?? 0,
        veh: row.veh ?? row.VEH ?? 0,
        yrblt: row.yrblt ?? row.YRBLT ?? row.BUILT ?? 0,
        fincp: row.fincp ?? row.FINCP ?? 0,
        grpip: row.grpip ?? row.GRPIP ?? 0,
        hincp: row.hincp ?? row.HINCP ?? 0,
        lngi: row.lngi ?? row.LNGI ?? 0,
        pwtgp: row.pwtgp ?? row.PWTGP ?? row.PWGTP ?? 0,
        agep: row.agep ?? row.AGEP ?? row.AGE ?? 0,
        eng: row.eng ?? row.ENG ?? 0,
        jwtrns: row.jwtrns ?? row.JWTRNS ?? 0,
        relshipp: row.relshipp ?? row.RELSHIPP ?? row.RELP ?? 0,
        schl: row.schl ?? row.SCHL ?? 0,
        sex: row.sex ?? row.SEX ?? 0,
        dis: row.dis ?? row.DIS ?? 0,
        hisp: row.hisp ?? row.HISP ?? 0,
        pincp: row.pincp ?? row.PINCP ?? 0,
        rac1p: row.rac1p ?? row.RAC1P ?? 0,
      }));

      console.log(`✅ Loaded ${results.length} records for region`);
      return results;
    } catch (error) {
      console.error('❌ Error fetching region PUMA data:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        throw new Error(`Failed to fetch region PUMA data: ${error.message}`);
      }
      throw new Error('Failed to fetch region PUMA data');
    }
  }

  /**
   * Get column information and unique values for filter building
   */
  async getColumnInfo(): Promise<{ [key: string]: any[] }> {
    try {
      console.log('🔍 Analyzing column data for filter building...');
      
      const allData = await this.getAllCaliforniaData();
      
      if (allData.length === 0) {
        return {};
      }

      const columnInfo: { [key: string]: any[] } = {};
      
      // Get unique values for each column
      Object.keys(allData[0]).forEach(column => {
        const uniqueValues = Array.from(new Set(allData.map(record => record[column as keyof CaliforniaPumaRecord])));
        columnInfo[column] = uniqueValues.sort((a, b) => {
          if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
          }
          return String(a).localeCompare(String(b));
        });
      });

      console.log('📊 Column analysis complete:', Object.keys(columnInfo));
      return columnInfo;
    } catch (error) {
      console.error('❌ Error analyzing column data:', error);
      throw new Error('Failed to analyze column data');
    }
  }
}

// Export singleton instance
export const californiaPumsService = new CaliforniaPumsService();
